<template>
  <b-container class="my-5">
    <div class="d-flex justify-content-center align-items-center" v-if="$apollo.loading">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <template v-else>
      <div class="d-flex flex-row justify-content-start align-items-center" v-if="isRecruiter">
        <h2 class="m-0">All Referral Jams</h2>
        <div class="flex-fill"/>
        <b-button @click.prevent="createNewEvent" :disabled="disableNewBtn" size="md" variant="outline-primary">
          <font-awesome-icon icon="plus"/>
          <span class="d-none d-sm-inline-block">{{ $t('ctas.plus-new') }}</span>
        </b-button>
      </div>
      <h2 v-else class="m-0">Your Referral Jams</h2>

      <b-list-group class="my-3">
        <b-list-group-item v-for="event in events" :key="event.id" :to="{ name: 'EventPage', params: { eventID: event.id } }">
          <b-media>
            <template #aside>
              <b-img v-if="isEventsSite && event.branding && event.branding.logo && event.branding.logo.fileLocation"
                class="rounded img-fit-contain" height="64" width="64" :src="event.branding.logo.fileLocation"/>
              <b-img v-else-if="event.branding && event.branding.socialImage && event.branding.socialImage.fileLocation"
                class="rounded img-fit-contain" height="64" width="64" :src="event.branding.socialImage.fileLocation"/>
              <b-img v-else-if="event.headline && event.headline.prizes && event.headline.prizes.first && event.headline.prizes.first.image && event.headline.prizes.first.image.fileLocation"
                class="rounded-circle img-fit-cover" height="64" width="64" :src="event.headline.prizes.first.image.fileLocation" :alt="event.headline.prizes.first.title || null"/>
              <b-img v-else blank :blank-color="event.branding && event.branding.primaryColour ? event.branding.primaryColour : '#ccc'" height="64" width="64"/>
            </template>

            <div class="d-flex flex-row justify-content-start align-items-center">
              <h5 class="m-0" v-if="isEventsSite && event.customFields">{{ event.customFields.companyName }} - {{ event.title }}</h5>
              <h5 class="m-0" v-else>{{ event.title }}</h5>
              <div class="flex-fill"/>
              <b-alert v-if="event.startDate || event.endDate" class="m-0 p-2" show variant="outline-primary">
                <template v-if="event.startDate">{{ event.startDate | formatDate('YYYY-MM-DD HH:mm') }}</template>
                <template v-if="event.startDate && event.endDate">&nbsp;&mdash;&nbsp;</template>
                <template v-if="event.endDate">{{ event.endDate | formatDate('YYYY-MM-DD HH:mm') }}</template>
              </b-alert>
              <b-dropdown v-if="isRecruiter" right no-caret variant="transparent">
                <template #button-content><font-awesome-icon icon="ellipsis-v"/></template>
                <b-dropdown-item :to="{ name: 'EventRoom', params: { eventID: event.id } }">Event Room</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'EventSettings', params: { eventID: event.id } }">Settings</b-dropdown-item>
                <b-dropdown-divider/>
                <b-dropdown-item :disabled="disableDuplicateBtn" href="#duplicate" @click.prevent="() => duplicateEvent(event)">Duplicate Event</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'EventSettings', params: { eventID: event.id, toDelete: true } }" variant="danger">Delete Event</b-dropdown-item>
              </b-dropdown>
            </div>

            <div v-if="event.description" v-html="event.description"/>
          </b-media>
        </b-list-group-item>
      </b-list-group>
    </template>
  </b-container>
</template>

<script>
import _pick from 'lodash/pick';
import gql from 'graphql-tag';
import { mapRoot } from '@/mixins/root';

const GetEventListingQuery = gql`
  query GetEventListing($isRecruiter: Boolean!, $where: EventWhereManyInput) {
    company @include(if: $isRecruiter) {
      id
      events(where: $where) {
        ...EventListing
      }
    }
    user @skip(if: $isRecruiter) {
      id
      events(where: $where) {
        ...EventListing
      }
    }
  }

  fragment EventListing on Event {
    id
    handle
    title
    description
    startDate
    endDate
    pageContent
    welcomeContent
    rulesContent

    branding {
      logo {
        id
        fileLocation
      }
      socialImage {
        id
        fileLocation
      }
      primaryColour
    }

    participants @include(if: $isRecruiter) {
      createdAt
    }

    leaderboard @include(if: $isRecruiter) {
      raffleMode

      prizes {
        first {
          title
          description
          image {
            id
          }
          url
          hidden
        }
        second {
          title
          description
          image {
            id
          }
          url
          hidden
        }
        third {
          title
          description
          image {
            id
          }
          url
          hidden
        }
        text
      }

      scores {
        id
        icon
        label
        eventNames
        value
      }
    }

    jobs @include(if: $isRecruiter) {
      id
    }
    contentShares @include(if: $isRecruiter) {
      id
    }

    customFields
    preferences

    createdAt
    updatedAt
    status
  }
`;
const CreateEventMutation = gql`
  mutation CreateEvent($create: CreateEventInput!) {
    event: createEvent(create: $create) {
      id
    }
  }
`;

export default {
  data: () => ({
    disableNewBtn: false,
    disableDuplicateBtn: false,
  }),
  apollo: {
    events: {
      query: GetEventListingQuery,
      variables() {
        const { isRecruiter } = this;
        return {
          isRecruiter,
          where: isRecruiter ? { status_in: [ 'ACTIVE', 'INACTIVE' ] } : { status: 'ACTIVE' },
        };
      },
      update({ company, user }) {
        const { isRecruiter } = this;
        const { events } = isRecruiter ? company : user;
        return Array.isArray(events) && events.length ? events : [];
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'isRecruiter', 'isEventsSite' ]),
  },
  methods: {
    async createNewEvent() {
      try {
        this.disableNewBtn = true;

        const { data, errors } = await this.$apollo.mutate({
          mutation: CreateEventMutation,
          variables: {
            create: {
              title: 'Referral Jam',
            },
          },
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('createEvent', err));
          throw errors.shift();
        }

        this.$assert(data && data.event && data.event.id, 404, 'Failed to create new event');
        const { event: { id: eventID } } = data;

        this.$router.push({ name: 'EventSettings', params: { eventID, isCreated: true } });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err, { prepend: 'Failed to create new Event' });
        this.disableNewBtn = false;
      }
    },
    async duplicateEvent(event) {
      try {
        this.disableDuplicateBtn = true;

        const create = _pick(event, [
          'description', 'startDate', 'endDate', 'pageContent', 'welcomeContent', 'rulesContent',
          'leaderboard', 'customFields', 'preferences',
        ]);

        create.title = `Copy of ${event.title}`;

        if (event.branding) {
          create.branding = { ...event.branding };
          [ 'logo', 'socialImage' ].forEach(key => {
            const { id: assetID } = event.branding[key] || {};
            create.branding[key] = assetID ? { id: assetID } : null;
          });
          delete create.branding.__typename;
        }

        if (event.leaderboard) {
          const { raffleMode, prizes, scores } = event.leaderboard;
          if (prizes) {
            [ 'first', 'second', 'third' ].forEach(key => {
              if (prizes && prizes[key] && prizes[key].image && prizes[key].image.id) {
                const { id: assetID } = prizes[key].image || {};
                prizes[key].image = assetID ? { id: assetID } : null;
                delete prizes[key].__typename;
              }
              delete prizes.__typename;
            });
          }
          if (Array.isArray(scores) && scores.length) {
            scores.forEach((score, i) => delete scores[i].__typename);
          }
          create.leaderboard = { raffleMode, prizes, scores };
        }

        [ 'jobs', 'contentShares' ].forEach(key => {
          if (Array.isArray(event[key]) && event[key].length) {
            create[key] = { id_in: event[key].map(({ id }) => id) };
          }
        });

        const { data, errors } = await this.$apollo.mutate({
          mutation: CreateEventMutation,
          variables: {
            create,
          },
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('duplicateEvent', err));
          throw errors.shift();
        }

        this.$assert(data && data.event && data.event.id, 404, 'Failed to create new event');
        const { event: { id: eventID } } = data;

        this.$router.push({ name: 'EventSettings', params: { eventID, isCreated: true } });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err, { prepend: 'Failed to create new Event' });
        this.disableDuplicateBtn = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/ReferralJam/EventList.vue